import { SelectChangeEvent } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '@verantos-dev/core-auth';
import { useGetCohortQuery } from '@verantos-dev/data-access-cohorts';
import {
  ExportConnectionEnum,
  FileTypeEnum,
  useGetExportConnectionsQuery,
  useExportCohortMutation,
} from '@verantos-dev/data-access-export-connections';
import { useGetProjectQuery } from '@verantos-dev/data-access-projects';
import { ExportCohortPayloadType, ExportConnectionDetailsType } from '@verantos-dev/types-cohorts';

import { useCohortModalContext } from '../context/modalContext';

export const getExportLocationDisplayText = (connection: ExportConnectionDetailsType) => {
  if (!connection) {
    return '';
  }
  switch (connection.connectionType) {
    case ExportConnectionEnum.SNOWFLAKE:
      return `Snowflake: ${connection.name}`;

    case ExportConnectionEnum.S3:
      return `s3://${connection.connectionDetails.bucket}/${connection.connectionDetails.region}`;

    default:
      return connection.name;
  }
};

export const useExportCohortModal = () => {
  const { itemId, closeDialog } = useCohortModalContext();
  const { projectId } = useParams<{ projectId: string }>();
  const [exportLocationId, setExportLocationId] = useState<ExportConnectionEnum>();
  const [schemaName, setSchemaName] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [fileType, setFileType] = useState<FileTypeEnum>(FileTypeEnum.CSV);
  const {
    user: { organizationId },
  } = useAuth();

  const { data, isLoading: loadingConnections } = useGetExportConnectionsQuery({
    organizationId: organizationId,
  });
  const { data: projectData, isLoading: loadingProject } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  });
  const { data: cohortData, isLoading: loadingCohort } = useGetCohortQuery(
    { cohortId: itemId || '', projectId: projectId || '' },
    {
      skip: !itemId || !projectId,
    }
  );
  const [exportCohortMutation, { isLoading, error: exportCohortError }] = useExportCohortMutation();

  useEffect(() => {
    if (cohortData?.data && projectData?.data) {
      const _concatText = `${projectData.data.projectName}_${cohortData.data.cohortName}`;
      const schemaProjectCohortText = _concatText.toUpperCase().replace(/[^a-zA-Z0-9]/g, '_');
      const fileProjectCohortText = _concatText.replace(/[^a-zA-Z0-9]/g, '_');
      const todayDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      setSchemaName(`${schemaProjectCohortText}_${todayDate}`);
      setFileName(`${fileProjectCohortText}_${todayDate}`);
    }
  }, [cohortData?.data, projectData?.data]);

  const exportLocationOptions = useMemo(() => {
    if (!data) return [];
    return data.map((connection) => ({
      value: connection.id,
      displayValue: getExportLocationDisplayText(connection),
    }));
  }, [data]);

  const selectedExportLocation = useMemo(() => {
    return data?.find((connection) => connection.id === exportLocationId);
  }, [data, exportLocationId]);

  const onChangeExportLocationId = (event: SelectChangeEvent<unknown>) => {
    setExportLocationId(event.target.value as ExportConnectionEnum);
  };

  const onChangeSchemaName = (_: string, value: string) => {
    setSchemaName(value);
  };

  const onChangeFileName = (_: string, value: string) => {
    setFileName(value);
  };

  const onChangeFileType = (value: FileTypeEnum) => {
    setFileType(value);
  };

  const onSubmit = async () => {
    if (projectId && itemId) {
      const data: ExportCohortPayloadType['data'] = {
        connectorId: exportLocationId as string,
      };
      if (selectedExportLocation?.connectionType === ExportConnectionEnum.SNOWFLAKE) {
        setSchemaName((prev) => prev.toUpperCase());
        data.connectorType = selectedExportLocation?.connectionType;
        data.connectorConfigurations = {
          targetSchemaName: schemaName.toUpperCase(),
        };
      } else if (selectedExportLocation?.connectionType === ExportConnectionEnum.S3) {
        data.connectorType = selectedExportLocation?.connectionType;
        data.connectorConfigurations = {
          fileName: fileName,
          fileType: fileType,
        };
      }
      await exportCohortMutation({
        projectId: projectId,
        cohortId: itemId,
        data,
      });
    }
  };

  const isValidForSubmit = Boolean(
    !(loadingCohort || loadingProject || loadingConnections) &&
      exportLocationId &&
      ((selectedExportLocation?.connectionType === ExportConnectionEnum.SNOWFLAKE && schemaName) ||
        (selectedExportLocation?.connectionType === ExportConnectionEnum.S3 &&
          fileName &&
          fileType))
  );

  return {
    isInitialLoading: loadingCohort || loadingProject || loadingConnections,
    isSubmitting: isLoading,
    isValidForSubmit,
    onClose: closeDialog,
    onSubmit,
    exportCohortError,

    projectName: projectData?.data?.projectName,
    cohortName: cohortData?.data.cohortName,

    exportLocationId,
    onChangeExportLocationId,
    selectedExportLocation,

    exportLocationOptions,
    schemaName,
    onChangeSchemaName,
    fileName,
    onChangeFileName,
    fileType,
    onChangeFileType,
  };
};
